import { useQuery } from '@tanstack/react-query';
import { useAuthContext } from '@/context/AuthContext';
import supabaseClient from '@/api/supabaseClient';
import { toast } from 'sonner';

export type UserRole = 'student' | 'admin' | 'super_admin';

interface AdminAuthResult {
  role: UserRole | null;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  loading: boolean;
  error: Error | null;
}

const fetchUserRole = async (userId: string | undefined): Promise<UserRole> => {
  if (!userId) {
    return 'student';
  }

  const { data, error } = await supabaseClient.from('users').select('role').eq('auth_id', userId).single();

  if (error) {
    console.error('Error fetching user role:', error);
    toast.error('Failed to load user permissions. Please try refreshing the page.');
    throw error;
  }

  return (data?.role as UserRole) || 'student';
};

export const useAdminAuth = (): AdminAuthResult => {
  const { session } = useAuthContext();
  const userId = session?.user?.id;

  const {
    data: role,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['userRole', userId],
    queryFn: () => fetchUserRole(userId),
    enabled: !!userId,
  });

  return {
    role: role || null,
    isAdmin: role === 'admin' || role === 'super_admin',
    isSuperAdmin: role === 'super_admin',
    loading: isLoading,
    error: error instanceof Error ? error : null,
  };
};
